import { useTranslation } from 'next-i18next'
import { parseCookies, setCookie } from 'nookies'
import { useCallback, useEffect, useId, useRef, useState } from 'react'
import useLogger from 'src/context/LoggerProvider/useLogger'
import { RedirectUrls } from 'src/types/types'
import fbp from 'src/utils/pixels/fbp'
import ga from 'src/utils/pixels/gtag'
import { getHsp } from 'src/utils/pixels/hubspot'
import posthog from 'src/utils/pixels/posthog'
import ms from 'src/utils/pixels/uetq'
import { safeStorageGet, safeStorageSet } from 'src/utils/safeStorage'
import {
  clearRejectedCookies,
  readStoredConsent
} from 'src/utils/storedCookieConsent'
import {
  bannerCollidesWithOneTap,
  CONSENT_BANNER_DISMISSED_KEY,
  CookieConsentAction,
  shouldShowConsentBanner
} from './consentBanner'
import {
  CONSENT_ACTIONS_CLASS,
  CONSENT_HEIGHT_VAR,
  CONSENT_HIDDEN_TRANSLATE,
  CONSENT_POSITION_CLASS,
  CONSENT_PRIMARY_CLASS,
  CONSENT_RULE_CLASS,
  CONSENT_SECONDARY_CLASS,
  CONSENT_SHEET_CLASS,
  CONSENT_TITLE_CLASS
} from './consentChrome'
import ManageCookiesModal, { IPreferences } from './ManageCookiesModal'

const COOKIE_SETTINGS = {
  maxAge: 365 * 24 * 60 * 60,
  path: '/'
}

interface CookieConsentProps {
  /**
   * Marks the banner as "not in the way" (see `IAuthContext.cookiesDealt`).
   * Called on every path that decides NOT to show the banner, on show when
   * the card cannot collide with One Tap (desktop), and on any decision or
   * dismiss. It is NOT a consent signal.
   */
  setCookiesDealt: (dealt: boolean) => void
}

// Banner-shown / accept / reject / save_preferences / dismiss all share the
// `cookie_consent` event name so a single PostHog breakdown on `action` shows
// the full funnel. Routed through `ga.event()` so the PostHog mirror picks it up.
const trackConsent = (
  action: CookieConsentAction,
  extra?: Partial<IPreferences>
) => ga.event('cookie_consent', { action, ...extra })

const CloseIcon = () => (
  <svg
    className="size-5"
    viewBox="0 0 20 20"
    fill="none"
    aria-hidden="true"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M5 5L15 15M15 5L5 15"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
)

const CookieConsentPopup: React.FC<CookieConsentProps> = ({
  setCookiesDealt
}) => {
  const { t } = useTranslation('common')
  const [isVisible, setIsVisible] = useState(false)
  const [isAnimating, setIsAnimating] = useState(false)
  const [showManageModal, setShowManageModal] = useState(false)
  const logger = useLogger()
  const titleId = useId()
  const bodyId = useId()
  const bannerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    // Every path that decides NOT to show the banner must mark it as "not in
    // the way" — otherwise One Tap stays disarmed for the whole SPA session.
    // Measured before this (PostHog, MX, 30d): One Tap sign-ups were 1.14% of
    // sessions on banner-shown pages, 0.32% on listing/hood pages and 0 on
    // Flotillas, where most paid search sessions land.
    if (!shouldShowConsentBanner(window.location.pathname)) {
      setCookiesDealt(true)
      return
    }

    if (safeStorageGet('session', CONSENT_BANNER_DISMISSED_KEY) === '1') {
      setCookiesDealt(true)
      return
    }

    const cookies = parseCookies()
    if (cookies['cookie-consent'] || cookies['cookie-consent-preferences']) {
      // A stored choice exists — initializeThirdParties re-applies it.
      setCookiesDealt(true)
      return
    }

    let shown = false

    const show = () => {
      if (shown) return
      shown = true
      cleanup()
      // Small additional delay for smooth entrance after interaction
      setTimeout(() => {
        setIsVisible(true)
        // Two frames, so the banner's FIRST paint is at the off-screen
        // translate. With one, the update committed before that paint: the
        // banner appeared in place (no slide) and its paragraph — about the
        // size of a city page's H1 — became the LCP element for first-time
        // visitors on slow Android, where 3.5 s is still inside the LCP
        // window. An element first painted off-screen is not an LCP
        // candidate, and the transform slide-in does not report a new one.
        requestAnimationFrame(() =>
          requestAnimationFrame(() => setIsAnimating(true))
        )
        trackConsent('shown')
        // Desktop: bottom-left card, One Tap renders top-right — no visual
        // collision, so arm it right away. Mobile: the card covers the bottom
        // of the viewport where One Tap's sheet lands; wait for a decision.
        if (!bannerCollidesWithOneTap(window.innerWidth)) setCookiesDealt(true)
      }, 300)
    }

    // Show after first user interaction (which also closes the LCP measurement window)
    const onInteraction = () => show()
    const interactionEvents = ['scroll', 'click', 'keydown', 'touchstart']
    interactionEvents.forEach((e) =>
      window.addEventListener(e, onInteraction, { once: true, passive: true })
    )

    // Fallback: show after 3.5s even without interaction (well past the LCP window)
    const fallbackTimer = setTimeout(show, 3500)

    const cleanup = () => {
      interactionEvents.forEach((e) =>
        window.removeEventListener(e, onInteraction)
      )
      clearTimeout(fallbackTimer)
    }

    return cleanup
  }, [setCookiesDealt])

  const initializeThirdParties = useCallback(() => {
    const stored = readStoredConsent()
    if (!stored) return
    const { analytics, advertisement, functionality } = stored

    const gtag = ga.getGtag()
    gtag('consent', 'update', {
      ad_storage: advertisement ? 'granted' : 'denied',
      ad_user_data: advertisement ? 'granted' : 'denied',
      ad_personalization: advertisement ? 'granted' : 'denied',
      analytics_storage: analytics ? 'granted' : 'denied',
      functionality_storage: functionality ? 'granted' : 'denied',
      personalization_storage: 'granted',
      security_storage: 'granted'
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } as any)

    // PostHog opt-in/opt-out — replaces the prior Clarity consent call
    // (removed 2026-05-13). Maps the granular `analytics` consent
    // dimension from the banner directly onto PostHog's single
    // opt_in_capturing / opt_out_capturing toggle. PostHog persists this
    // choice in its own cookie so it sticks across visits — no need to
    // re-call on every page load.
    //
    // Safe to call before `posthog.init()` resolves: `setConsent` goes
    // through the `runOrQueue` buffer in `posthog.ts` and is drained in
    // order once the dynamic import completes.
    posthog.setConsent(analytics)

    ms.getUetq().push('consent', 'update', {
      ad_storage: advertisement ? 'granted' : 'denied'
    })

    // Meta pixel — the pixel does NOT persist `fbq('consent', …)` across
    // loads (it boots granted every time), so the stored `advertisement`
    // choice is re-applied here on every mount, not only on the click.
    fbp.setConsent(advertisement)

    // Consent modes stop NEW cookies; this removes the ones set before the
    // choice (first visit, page 1) — on every load, not only on the click.
    clearRejectedCookies()

    setCookiesDealt(true)
  }, [setCookiesDealt])

  useEffect(() => {
    try {
      initializeThirdParties()
    } catch (error) {
      if (logger) logger.error('Error initializing third parties', error)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initializeThirdParties])

  const handleClose = useCallback(() => {
    setIsAnimating(false)
    setTimeout(() => setIsVisible(false), 300)
  }, [])

  const handleAccept = () => {
    trackConsent('accept')
    setCookie(null, 'cookie-consent', 'true', COOKIE_SETTINGS)
    handleClose()
    initializeThirdParties()
  }

  const handleReject = () => {
    trackConsent('reject')
    setCookie(null, 'cookie-consent', 'false', COOKIE_SETTINGS)

    const _hsp = getHsp()
    _hsp.push(['revokeCookieConsent'])

    initializeThirdParties()
    handleClose()
    setCookiesDealt(true)
  }

  const onSavePreferences = (preferences: IPreferences) => {
    trackConsent('save_preferences', preferences)
    const str = JSON.stringify(preferences)
    setCookie(null, 'cookie-consent-preferences', str, COOKIE_SETTINGS)

    // Same sink as reject — HubSpot only learns "analytics off" via revoke.
    if (!preferences.analytics) getHsp().push(['revokeCookieConsent'])

    initializeThirdParties()
    setShowManageModal(false)
    handleClose()
  }

  // Dismiss (X / Escape) is NOT a consent choice: no cookie is written, the
  // banner just stops being in the way for this tab. A new visit shows it
  // again; SPA navigation within the session does not.
  const handleDismiss = useCallback(() => {
    trackConsent('dismiss')
    safeStorageSet('session', CONSENT_BANNER_DISMISSED_KEY, '1')
    handleClose()
    setCookiesDealt(true)
  }, [handleClose, setCookiesDealt])

  useEffect(() => {
    // `isAnimating` drops synchronously on any decision, which also removes
    // the listener — so a second Escape during the exit animation is a no-op.
    if (!isVisible || !isAnimating || showManageModal) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') handleDismiss()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [isVisible, isAnimating, showManageModal, handleDismiss])

  // Publish the slip's height as `--consent-h` on <html> while it is on
  // screen, so in-flow CTAs (the on-demand wizard's «Continuar») can reserve
  // room under it on mobile. One ResizeObserver, no polling; `offsetHeight`
  // ignores the slide-in transform. Cleared on hide, on switching to the
  // preferences sheet, and on unmount.
  useEffect(() => {
    const banner = bannerRef.current
    if (!isVisible || showManageModal || !banner) return
    const root = document.documentElement
    const publish = () =>
      root.style.setProperty(CONSENT_HEIGHT_VAR, `${banner.offsetHeight}px`)
    publish()
    const observer =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(publish)
    observer?.observe(banner)
    return () => {
      observer?.disconnect()
      root.style.removeProperty(CONSENT_HEIGHT_VAR)
    }
  }, [isVisible, showManageModal])

  if (!isVisible) return null

  if (showManageModal) {
    return (
      <ManageCookiesModal
        onSave={onSavePreferences}
        onRejectAll={handleReject}
        onClose={() => setShowManageModal(false)}
      />
    )
  }

  // Non-modal banner (WCAG: no focus trap, focus is not moved on show). There
  // is deliberately NO backdrop: the previous full-screen mobile backdrop
  // turned every tap outside the card — including the hero search input
  // sitting right above it — into "Aceptar todas" and swallowed the tap.
  //
  // Editorial slip, not a SaaS card: blue rule, Fraunces title, one flowing
  // paragraph, AA-clean actions. On a 320px phone it is ~45px SHORTER than
  // the old shield-icon card (no 40px icon column forcing the title and
  // subtitle to wrap, no side/bottom inset) while every action stays 44px.
  return (
    <div
      ref={bannerRef}
      role="dialog"
      aria-modal="false"
      aria-labelledby={titleId}
      aria-describedby={bodyId}
      className={`${CONSENT_POSITION_CLASS} transition-transform duration-300 motion-reduce:transition-none ${
        isAnimating ? 'translate-y-0' : CONSENT_HIDDEN_TRANSLATE
      }`}
    >
      {/* Transform-only animation keeps colours at full opacity so contrast
          is measured correctly. */}
      <div className={CONSENT_SHEET_CLASS}>
        <div className={CONSENT_RULE_CLASS} aria-hidden="true" />

        <div className="px-4 pt-3 md:px-6 md:pt-5">
          <div className="flex items-start gap-3">
            <h3
              id={titleId}
              className={`min-w-0 flex-1 pt-1.5 text-[18px] md:pt-2 md:text-[21px] ${CONSENT_TITLE_CLASS}`}
            >
              {t('COOKIES.TITLE')}
            </h3>
            <button
              type="button"
              onClick={handleDismiss}
              aria-label={t('COOKIES.CLOSE')}
              className="-mr-2.5 flex size-11 shrink-0 items-center justify-center rounded-full text-ink-500 transition-colors duration-200 hover:bg-cream-300 hover:text-ink-700 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ink-700 md:-mr-3"
            >
              <CloseIcon />
            </button>
          </div>

          {/* Subtitle + body flow as one paragraph; `bodyId` still wraps
              exactly the body sentence + privacy link it described before. */}
          <p className="text-[13px] leading-snug text-ink-200 md:mt-1 md:text-sm md:leading-relaxed">
            {`${t('COOKIES.SUBTITLE')} `}
            <span id={bodyId}>
              {`${t('COOKIES.BODY')} `}
              <a
                href={RedirectUrls.Disclosures}
                className="rounded-sm font-medium text-blue-800 underline decoration-blue-800/30 decoration-1 underline-offset-2 transition-colors duration-200 hover:decoration-blue-800 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink-700"
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('COOKIES.PRIVACY_LINK')}
              </a>
            </span>
          </p>

          <div className={`mt-3 md:mt-5 md:pb-6 ${CONSENT_ACTIONS_CLASS}`}>
            <button
              type="button"
              onClick={handleAccept}
              className={CONSENT_PRIMARY_CLASS}
            >
              {t('COOKIES.ACCEPT_ALL')}
            </button>
            <button
              type="button"
              onClick={() => setShowManageModal(true)}
              className={CONSENT_SECONDARY_CLASS}
            >
              {t('COOKIES.CONFIGURE')}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CookieConsentPopup
